/**
 * Zod schema + form-values type for the Personal Details tab.
 *
 * The form values are the patient-shaped payload we ship to
 * `useCreatePatient` / `useUpdatePatient`. Sub-objects (`identification`,
 * `patientEducation`, `patientOccupation`, `guardianIdenitification`) live
 * under their own keys so each section can edit a focused slice.
 *
 * Required-field policy mirrors Flutter `EnablePatientSaveState`:
 *   firstName, lastName, gender (master-data GUID), birthDate, phoneNumber
 *
 * Optional fields that pass through to the wire shape with `null` when
 * unset — `email`, `photoUrl`, etc. — are typed as `string | null`.
 *
 * Wire-shape rules (verified curl from PROD-708):
 *   - Dates ship as `YYYY-MM-DDTHH:mm:ss.sss` (no `Z`)
 *   - `gender` is the GUID; `genderName` is the full `{id,name}` echo
 *   - `phoneNumber` is `"+<dial>-<digits>"`
 */

import { z } from 'zod';

/** Master-data id/name pair. Patient API returns these with both fields
 *  nullable; we mirror that so re-projecting a `Patient` doesn't break
 *  the resolver type-check on objects whose backend payload is sparse. */
const idNameSchema = z.object({
  id: z.string().nullable(),
  name: z.string().nullable(),
});

const educationSchema = z
  .object({
    id: z.string().nullable().optional(),
    patientId: z.string().nullable().optional(),
    educationCode: z.string().nullable().optional(),
    educationDisplay: z.string().nullable().optional(),
    educationSystem: z.string().nullable().optional(),
    educationInformation: z.string().nullable().optional(),
  })
  .nullable();

const occupationSchema = z
  .object({
    id: z.string().nullable().optional(),
    patientId: z.string().nullable().optional(),
    employment: z.string().nullable().optional(),
    jobRole: z.string().nullable().optional(),
    occupationalInformation: z.string().nullable().optional(),
  })
  .nullable();

/**
 * Identification block — wire shape verified via PUT/POST curls
 * (2026-05-14). The real keys are `id, idType, idNumber,
 * idExpirationDate, submittedDate` (NOT `expiryDate`, NOT `idTypeName`).
 * `id` ships as `""` on create.
 *
 * **Phantom fields preserved for round-trip safety** (Samer review
 * 2026-05-14): `idTypeName`, `issueDate`, `expiryDate`, `issuingCountry`
 * have NO observed wire support, but PROD-725 shipped UI for them
 * before this fix. Until a GET-response curl confirms they're truly
 * absent from the wire (not just optional/nullable), we keep them on
 * the schema as optional so a server-supplied value round-trips back
 * unchanged via `toPatientBody`. The UI for them is gone (Issue date /
 * Issuing country / idTypeName); the schema retains them as a safety
 * net.
 */
const identificationSchema = z
  .object({
    id: z.string().nullable().optional(),
    idType: z.string().nullable().optional(),
    idNumber: z.string().nullable().optional(),
    idExpirationDate: z.string().nullable().optional(),
    submittedDate: z.string().nullable().optional(),
    // Phantom — kept for round-trip safety, no UI:
    idTypeName: idNameSchema.nullable().optional(),
    issueDate: z.string().nullable().optional(),
    expiryDate: z.string().nullable().optional(),
    issuingCountry: z
      .object({
        code: z.string().nullable().optional(),
        name: z.string().nullable().optional(),
      })
      .nullable()
      .optional(),
  })
  .nullable();

/**
 * Guardian identification — wire-level typo `relationShip` preserved
 * (camelCase capital S). Verified via PUT/POST curls. Has its own `id`
 * + `idExpirationDate` (separate from the patient's identification).
 *
 * **Phantom fields preserved for round-trip safety** (Samer review
 * 2026-05-14): `relationship` (lowercase), `email`, `contactNumber`
 * have no observed wire support. UI for email/contact removed;
 * `relationship` no longer written (the new `relationShip` field is
 * authoritative). All three retained on the schema as optional so a
 * server-supplied value round-trips back unchanged.
 */
const guardianSchema = z
  .object({
    id: z.string().nullable().optional(),
    firstName: z.string().nullable().optional(),
    middleName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    birthDate: z.string().nullable().optional(),
    gender: z.string().nullable().optional(),
    idType: z.string().nullable().optional(),
    idNumber: z.string().nullable().optional(),
    idExpirationDate: z.string().nullable().optional(),
    /** Wire-level typo: camelCase capital S. */
    relationShip: idNameSchema.nullable().optional(),
    relationId: z.string().nullable().optional(),
    // Phantom — kept for round-trip safety, no UI:
    relationship: idNameSchema.nullable().optional(),
    email: z.string().nullable().optional(),
    contactNumber: z.string().nullable().optional(),
  })
  .nullable();

/**
 * Importance ({code, display, definition}) — verified via PUT curl.
 * The form holds it as nullable; consumers reading display surfaces
 * read `.code` or `.display`.
 */
const importanceSchema = z
  .object({
    definition: z.string().nullable().optional(),
    code: z.string().nullable().optional(),
    display: z.string().nullable().optional(),
  })
  .nullable();

/**
 * Residency type ({code, display, definition}) — verified via PUT curl.
 */
const residencyTypeSchema = z
  .object({
    code: z.string().nullable().optional(),
    display: z.string().nullable().optional(),
    definition: z.string().nullable().optional(),
  })
  .nullable();

/**
 * Patient consent ({code, display, definition}) — HIE data-sharing
 * consent. Verified PUT-curl shape (patient-api.ts PatientConsent).
 */
const consentSchema = z
  .object({
    definition: z.string().nullable().optional(),
    code: z.string().nullable().optional(),
    display: z.string().nullable().optional(),
  })
  .nullable();

/**
 * Nationality ({code, display, system}) — NationalityCase. `display` is
 * the country name; `code` is the nationality label (e.g. "Indian").
 */
const nationalitySchema = z
  .object({
    code: z.string().nullable(),
    display: z.string().nullable(),
    system: z.string().nullable().optional(),
  })
  .nullable();

/**
 * Religion ({code, display, system, description, shortName}) —
 * CodeWordCase. `display` is the label (e.g. "Christian").
 */
const religionSchema = z
  .object({
    code: z.string().nullable(),
    display: z.string().nullable(),
    system: z.string().nullable().optional(),
    description: z.string().nullable().optional(),
    shortName: z.string().nullable().optional(),
  })
  .nullable();

/**
 * Email — REQUIRED + format-checked (F-PAT-236). Flutter
 * `create_edit_patient_bloc.dart:230` gates the patient Save on
 * `!isBlank(patient.email)` alongside `!isBlank(patient.phoneNumber)`
 * (Andrew confirmed 2026-06-06: email AND phone are both required; no
 * blank-email walk-in carve-out). Kept nullable at the type level so the
 * field stays `string | null` for hydration/initial values — validity is
 * enforced by the refinements: blank → "Email is required", present but
 * malformed → "Enter a valid email address."
 */
const requiredEmail = z
  .string()
  .nullable()
  .refine((v) => !!v && v.trim().length > 0, 'Email is required')
  .refine(
    (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v),
    'Enter a valid email address.',
  );

export const personalDetailsSchema = z.object({
  // Context (carried through the form, not user-editable)
  organizationId: z.string(),
  practiceId: z.string(),

  // Identity (must-have for save)
  firstName: z.string().trim().min(1, 'First name is required'),
  middleName: z.string().trim().nullable(),
  lastName: z.string().trim().min(1, 'Last name is required'),
  birthDate: z.string().min(1, 'Date of birth is required'),
  gender: z.string().min(1, 'Gender is required'),
  genderName: idNameSchema.nullable(),

  // Contact — both required (F-PAT-236; Flutter create_edit_patient_bloc.dart:230).
  email: requiredEmail,
  phoneNumber: z.string().min(1, 'Primary contact is required'),

  // Demographic extras
  maritalStatus: z.string().nullable(),
  maritalStatusName: idNameSchema.nullable().optional(),
  language: z.string().nullable(),
  languageName: idNameSchema.nullable(),
  interpreterRequired: z.boolean().nullable(),
  isAdult: z.boolean(),

  // Status / read-only
  patientMrn: z.string().nullable(),
  externalMRN: z.string().nullable(),
  isActive: z.boolean().nullable(),
  isMerged: z.boolean().nullable(),
  isMissingData: z.boolean().nullable(),
  lastUpdated: z.string().nullable(),

  // Photo + ID card images (hosted URLs from /api/Master/UploadImage).
  // The save body carries the ID-card URLs on frontIdCardUrl/backIdCardUrl
  // (PUT-curl-verified) — NOT the legacy idFrontImage/idBackImage.
  photoUrl: z.string().nullable(),
  frontIdCardUrl: z.string().nullable(),
  backIdCardUrl: z.string().nullable(),

  // Lookup objects
  importance: importanceSchema,
  residencyType: residencyTypeSchema,
  consent: consentSchema,
  nationality: nationalitySchema,
  religion: religionSchema,

  // Sub-resources (identity, education, occupation, guardian)
  identification: identificationSchema,
  patientEducation: educationSchema,
  patientOccupation: occupationSchema,
  guardianIdenitification: guardianSchema,
});

export type PersonalDetailsFormValues = z.infer<typeof personalDetailsSchema>;

/** Default values for create-mode: matches the Flutter initial state in
 *  `create_patient_screen.dart:116-134`. */
export function defaultFormValues(
  organizationId: string,
  practiceId: string,
): PersonalDetailsFormValues {
  return {
    organizationId,
    practiceId,
    firstName: '',
    middleName: null,
    lastName: '',
    birthDate: '',
    gender: '',
    genderName: null,
    email: null,
    phoneNumber: '',
    maritalStatus: null,
    maritalStatusName: null,
    language: null,
    languageName: null,
    interpreterRequired: null,
    isAdult: true,
    patientMrn: null,
    externalMRN: null,
    isActive: null,
    isMerged: null,
    isMissingData: null,
    lastUpdated: null,
    photoUrl: null,
    frontIdCardUrl: null,
    backIdCardUrl: null,
    importance: null,
    residencyType: null,
    // Consent defaults to "Yes / Opt in" (code "0") in create-mode —
    // Flutter `personal_details_form.dart:117-119` seeds consentList[0].
    consent: { code: '0', display: 'Yes', definition: 'Opt in' },
    nationality: null,
    religion: null,
    identification: null,
    patientEducation: null,
    patientOccupation: null,
    guardianIdenitification: null,
  };
}
